/**
 * YHONE Size Guide Enhancements
 * Adds row focus, product-size synchronization, and custom horizontal navigation
 * without modifying the vendor-owned Fit Check size-guide controller.
 */
(function () {
  'use strict';

  if (window.__yhoneSizeGuideEnhancementsLoaded) return;
  window.__yhoneSizeGuideEnhancementsLoaded = true;

  const states = new WeakMap();

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();

  function rowLabel(row) {
    return row.querySelector('th[scope="row"]')?.textContent?.trim() || '';
  }

  function highlight(state, value) {
    const key = normalize(value);
    const label = state.sizes.get(key);
    if (!label) return;

    for (const row of state.root.querySelectorAll('.fit-sg__table tbody tr')) {
      const selected = row.dataset.fitSgSize === key;
      row.classList.toggle('is-selected', selected);
      const heading = row.querySelector('th[scope="row"]');
      if (selected) heading?.setAttribute('aria-current', 'true');
      else heading?.removeAttribute('aria-current');
    }

    if (state.select instanceof HTMLSelectElement) state.select.value = label;
  }

  function syncFromOptions(state, options) {
    if (!Array.isArray(options)) return;
    const values = options.map((option) =>
      typeof option === 'string' ? option : option?.value || option?.name || ''
    );
    const match = values.find((value) => state.sizes.has(normalize(value)));
    if (match) highlight(state, match);
  }

  function initialOptions(root) {
    try {
      const value = JSON.parse(root.dataset.selectedOptions || '[]');
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }

  function updateScroll(state) {
    const { scroll, nav, track, thumb, buttons } = state;
    if (!scroll || !nav || !track || !thumb) return;

    const max = Math.max(0, scroll.scrollWidth - scroll.clientWidth);
    const hasOverflow = max > 1;
    nav.hidden = !hasOverflow;
    if (!hasOverflow) return;

    const left = Math.min(max, Math.max(0, scroll.scrollLeft));
    const trackWidth = track.clientWidth;
    const visibleRatio = Math.min(1, scroll.clientWidth / scroll.scrollWidth);
    const thumbWidth = Math.max(32, trackWidth * visibleRatio);
    const travel = Math.max(0, trackWidth - thumbWidth);
    const progress = max ? left / max : 0;

    thumb.style.width = thumbWidth + 'px';
    thumb.style.transform = 'translateX(' + (travel * progress) + 'px)';

    if (buttons[0] instanceof HTMLButtonElement) buttons[0].disabled = left <= 1;
    if (buttons[1] instanceof HTMLButtonElement) buttons[1].disabled = left >= max - 1;
  }

  function init(root) {
    if (!(root instanceof HTMLElement) || states.has(root)) return;

    const scroll = root.querySelector('.fit-sg__table-scroll');
    const source = root.querySelector('[data-fit-sg-table="source"]');
    const select = root.querySelector('[data-fit-sg-size-select]');
    const focus = root.querySelector('[data-fit-sg-size-focus]');
    const nav = root.querySelector('[data-fit-sg-scroll-nav]');
    const track = root.querySelector('[data-fit-sg-scroll-track]');
    const thumb = root.querySelector('[data-fit-sg-scroll-thumb]');
    const buttons = Array.from(root.querySelectorAll('[data-fit-sg-scroll-step]'));
    if (!(scroll instanceof HTMLElement) || !(source instanceof HTMLTableElement)) return;

    const controller = new AbortController();
    const sizes = new Map();
    const state = { root, scroll, source, select, focus, nav, track, thumb, buttons, sizes, controller, resizeObserver: null };
    states.set(root, state);
    root.dataset.yhoneEnhanced = 'true';

    for (const row of source.querySelectorAll('tbody tr')) {
      const label = rowLabel(row);
      const key = normalize(label);
      if (key && !sizes.has(key)) sizes.set(key, label);
    }

    if (select instanceof HTMLSelectElement) {
      for (const label of sizes.values()) {
        const option = document.createElement('option');
        option.value = label;
        option.textContent = label;
        select.append(option);
      }
      select.addEventListener('change', () => highlight(state, select.value), { signal: controller.signal });
    }

    if (focus instanceof HTMLElement && sizes.size) focus.hidden = false;

    for (const row of root.querySelectorAll('.fit-sg__table tbody tr')) {
      row.dataset.fitSgSize = normalize(rowLabel(row));
    }

    scroll.addEventListener('click', (event) => {
      const row = event.target instanceof Element ? event.target.closest('tbody tr') : null;
      if (row instanceof HTMLTableRowElement && root.contains(row)) highlight(state, rowLabel(row));
    }, { signal: controller.signal });

    scroll.addEventListener('scroll', () => updateScroll(state), {
      passive: true,
      signal: controller.signal,
    });

    for (const button of buttons) {
      button.addEventListener('click', () => {
        const direction = Number(button.dataset.fitSgScrollStep || 0);
        if (!direction) return;
        scroll.scrollBy({
          left: direction * Math.max(scroll.clientWidth * 0.72, 120),
          behavior: 'smooth',
        });
      }, { signal: controller.signal });
    }

    track?.addEventListener('click', (event) => {
      const rect = track.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      const max = Math.max(0, scroll.scrollWidth - scroll.clientWidth);
      scroll.scrollTo({ left: max * ratio, behavior: 'smooth' });
    }, { signal: controller.signal });

    root.querySelector('[data-fit-sg-toggle]')?.addEventListener(
      'click',
      () => requestAnimationFrame(() => updateScroll(state)),
      { signal: controller.signal }
    );

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(() => updateScroll(state));
      state.resizeObserver.observe(scroll);
    } else {
      window.addEventListener('resize', () => updateScroll(state), { signal: controller.signal });
    }

    syncFromOptions(state, initialOptions(root));
    requestAnimationFrame(() => updateScroll(state));
  }

  function destroy(root) {
    const state = states.get(root);
    if (!state) return;
    state.controller.abort();
    state.resizeObserver?.disconnect();
    states.delete(root);
  }

  function scan(scope) {
    if (scope instanceof Element && scope.matches('fit-size-guide')) init(scope);
    scope.querySelectorAll?.('fit-size-guide').forEach(init);
  }

  function eventMatches(state, event, productId) {
    if (event?.target instanceof Element && event.target.closest('product-card, quick-add-dialog')) return false;
    const own = String(state.root.dataset.productId || '').trim();
    const incoming = String(productId || '').trim();
    return !own || !incoming || own === incoming;
  }

  document.addEventListener('variant:pick', (event) => {
    const resource = event?.detail?.resource;
    document.querySelectorAll('fit-size-guide[data-yhone-enhanced]').forEach((root) => {
      const state = states.get(root);
      if (state && eventMatches(state, event, resource?.productId)) syncFromOptions(state, resource?.selectedOptions);
    });
  });

  document.addEventListener('variant:change', (event) => {
    document.querySelectorAll('fit-size-guide[data-yhone-enhanced]').forEach((root) => {
      const state = states.get(root);
      if (state && eventMatches(state, event, event?.detail?.data?.productId)) {
        syncFromOptions(state, event?.detail?.resource?.options);
      }
    });
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) if (node instanceof Element) scan(node);
      for (const node of mutation.removedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('fit-size-guide')) destroy(node);
        node.querySelectorAll?.('fit-size-guide').forEach(destroy);
      }
    }
  });

  const start = () => {
    scan(document);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
